import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spinner, Badge, Form, InputGroup, Pagination, Dropdown, Row, Col, Modal, Alert } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaEllipsisV, FaCheckCircle, FaTimesCircle, FaEye, FaUserPlus,
         FaUser, FaEnvelope, FaPhone, FaPlus, FaChartLine, FaCreditCard, FaHome, FaMoneyBillWave,
         FaFileUpload, FaDownload } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { dsoApi, productApi } from '../../services/api';
import AddLeadForm from '../../components/Leads/AddLeadForm'; // Import the new component
import BulkUploadModal from '../../components/Leads/BulkUploadModal'; // Import Bulk Upload Modal
import './Leads.css';

// Case status mapping
const caseStatusMap = {
  1: { label: 'Fresh', variant: 'primary' },
  11: { label: 'Rejected', variant: 'danger' },
  13: { label: 'Disbursed', variant: 'success' },
  13: { label: 'Disbursed', variant: 'success' },
};

// Case type mapping
const caseTypeMap = {
  'Credit Card': 'Credit Card',
  'Home Loan': 'Home Loan',
  'Personal Loan': 'Personal Loan',
  'Business Loan': 'Business Loan',
  'LAP': 'LAP'
};

function DSOLeads({ onLeadsUpdate }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [
    { value: 5, label: '5 rows' },
    { value: 10, label: '10 rows' },
    { value: 25, label: '25 rows' },
    { value: 50, label: '50 rows' },
  ];

  // States for Add Lead Modal (now controlled via AddLeadForm)
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [products, setProducts] = useState([]); // Keep products state here
  const [loadingProducts, setLoadingProducts] = useState(false); // Keep loading state here

  // State for bulk upload modal visibility
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  // Removed: bulkFile, uploadingBulk, bulkUploadError, selectedAgentForBulk

  useEffect(() => {
    // Get query parameters
    const agent_id = searchParams.get('agent_id');
    if (agent_id) {
      setAgentFilter(agent_id);
    }
    
    fetchAgents();
    fetchLeads();
    // fetchProducts(1); // No longer needed here, AddLeadForm fetches initial products
  }, [searchParams, currentPage, pageSize, searchTerm, statusFilter, agentFilter]);

  const fetchAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await dsoApi.getMyAgents({ limit: 100 }); // Get all agents
      setAgents(response.data.items || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
      // We'll just log the error but not display it to the user
    } finally {
      setLoadingAgents(false);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    
    // Build query parameters
    const params = {
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
    };
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    if (agentFilter) {
      params.agent_id = agentFilter;
    }
    
    try {
      const response = await dsoApi.getLeads(params);
      console.log('Leads fetched:', response.data);
      setLeads(response.data.items || []);
      setTotalLeads(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load leads. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when currentPage changes
  };

  const handleFilterChange = (filterType, selectedOption) => {
    if (filterType === 'status') {
      const value = selectedOption ? selectedOption.value : '';
      setStatusFilter(value);
    } else if (filterType === 'agent') {
      const value = selectedOption ? selectedOption.value : '';
      setAgentFilter(value);
      
      // Update URL query parameter
      if (value) {
        searchParams.set('agent_id', value);
      } else {
        searchParams.delete('agent_id');
      }
      setSearchParams(searchParams);
    }
    
    // Reset to first page when changing filters
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when the dependencies change
  };

  // Function to manually trigger a refresh of the leads data
  const refreshLeads = () => {
    fetchLeads();
  };

  // fetchProducts remains here, passed as prop to AddLeadForm
  const fetchProducts = useCallback(async (categoryId = null) => {
    setLoadingProducts(true);
    try {
      // Use a high limit initially to fetch all products for grouping
      // Pagination can be added later if needed
      const params = { limit: 500 }; // Ensure limit is set
      if (categoryId !== null) {
        params.category_id = categoryId;
      }
      const response = await productApi.getProducts(params);
      setProducts(response.data.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]); // Clear products on error
    } finally {
      setLoadingProducts(false);
    }
  }, []); // Empty dependency array as setProducts/setLoadingProducts are stable

  // New handler to process submission from AddLeadForm
  const handleAddLeadSubmit = async (leadData, agentId) => {
    // The AddLeadForm component handles loading state internally
    // We just need to call the API and handle success/error
    try {
      console.log('Creating lead via AddLeadForm for agent ID:', agentId, 'with data:', leadData);
      await dsoApi.createLeadForAgent(agentId, leadData);

      // Close modal
      setShowAddLeadModal(false);

      // Refresh leads list
      refreshLeads();

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Lead created and assigned successfully',
        timer: 2000,
        showConfirmButton: false
      });

      // Trigger an event for other components
      window.dispatchEvent(new CustomEvent('lead_added', {
        detail: { agentId: agentId }
      }));

    } catch (err) {
      console.error('Error creating lead from AddLeadForm:', err);
      // Re-throw the error so AddLeadForm can display it
      throw err;
    }
  };

  // New handler for BulkUploadModal submission
  const handleBulkUploadSubmit = useCallback(async (file, agentId) => {
    console.log('Submitting bulk upload via BulkUploadModal for agent ID:', agentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('agent_id', agentId);

      const response = await dsoApi.bulkUploadLeads(formData);

      // Close modal on success
      setShowBulkUploadModal(false);

      // Refresh leads list
      refreshLeads();

      // Show success message with counts (no timer, requires user confirmation)
      Swal.fire({
        icon: 'success',
        title: 'Bulk Upload Successful',
        text: `Successfully processed ${response.data.total_records || response.data.total} leads: ${response.data.successful || response.data.success} created, ${response.data.duplicates} duplicates, ${response.data.skipped} skipped, ${response.data.failed} failed.`,
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });

      // Trigger event
      window.dispatchEvent(new CustomEvent('leads_bulk_added', {
        detail: { agentId: agentId }
      }));

    } catch (err) {
      console.error('Error in handleBulkUploadSubmit:', err);
      // Re-throw the error so the modal can display it
      throw new Error(err.response?.data?.detail || err.message || 'Bulk upload failed.');
    }
  }, [refreshLeads]); // Add refreshLeads to dependency array


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (selectedOption) => {
    const newPageSize = selectedOption.value;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    // fetchLeads will be triggered by the effect hook
  };

  const handleViewLead = (leadId) => {
    // Navigate to lead edit page
    navigate(`/dso/leads/${leadId}/edit`);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalLeads / pageSize);
    if (totalPages <= 1 && totalLeads <= pageSize) return null;

    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalLeads);

    const pageButtons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(startPage + maxButtons - 1, totalPages);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      pageButtons.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
          className={number === currentPage ? 'active' : ''}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <div className="dso-pagination-container">
        <div className="dso-pagination-info-bar">
          <div className="dso-pagination-info">
            Showing {startRecord} to {endRecord} of {totalLeads} records
          </div>
          
          <div className="dso-pagination-pagesize">
            <span className="me-2">Rows per page:</span>
            <Select
              className="dso-pagesize-select"
              classNamePrefix="react-select"
              options={pageSizeOptions}
              value={pageSizeOptions.find(option => option.value === pageSize)}
              onChange={handlePageSizeChange}
              isSearchable={false}
              menuPlacement="top"
            />
          </div>
        </div>
        
        <div className="dso-pagination-controls">
          <Pagination>
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
              className="pagination-first"
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              className="pagination-prev"
            />
            
            {startPage > 1 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                {startPage > 2 && <Pagination.Ellipsis />}
              </>
            )}
            
            {pageButtons}
            
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <Pagination.Ellipsis />}
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="pagination-next"
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
              className="pagination-last"
            />
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="dso-leads-container">
        <div className="dso-leads-header">
          <h3>Leads</h3>
        <div className="dso-leads-actions">
          <Button 
            variant="outline-secondary" 
            className="me-2"
            onClick={() => setShowBulkUploadModal(true)}
          >
            <FaFileUpload className="me-1"/> Bulk Upload
          </Button>
          <Button 
            variant="primary"
            onClick={() => setShowAddLeadModal(true)}
          >
            <FaUserPlus className="me-1"/> Add Lead
          </Button>
        </div>
      </div>

      <Row className="dso-leads-filters-row align-items-center">
        <Col xs={12} md={6}>
          <Form onSubmit={handleSearch} className="dso-search-form">
            <InputGroup size="sm">
              <Form.Control
                placeholder="Search name, phone, or application #"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>
        </Col>
        
        <Col xs={12} md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
          <div className="dso-filter-group">
            <div className="dso-select-container me-2">
              <label className="dso-filter-label">Status:</label>
              <Select
                className="dso-select"
                classNamePrefix="react-select"
                placeholder="All Statuses"
                isClearable
                isSearchable={false}
                value={statusFilter ? { label: caseStatusMap[statusFilter]?.label, value: statusFilter } : null}
                onChange={(selectedOption) => handleFilterChange('status', selectedOption)}
                options={[
                  { value: '', label: 'All' },
                  ...Object.entries(caseStatusMap).map(([key, { label }]) => ({
                    value: key,
                    label: label
                  }))
                ]}
              />
            </div>
            
            <div className="dso-select-container">
              <label className="dso-filter-label">Agent:</label>
              <Select
                className="dso-select"
                classNamePrefix="react-select"
                placeholder="All Agents"
                isClearable
                isLoading={loadingAgents}
                value={agentFilter ? 
                  { 
                    label: agents.find(a => a.id === Number(agentFilter))?.name || 'Loading...', 
                    value: agentFilter 
                  } : null
                }
                onChange={(selectedOption) => handleFilterChange('agent', selectedOption)}
                options={[
                  { value: '', label: 'All Agents' },
                  ...agents.map(agent => ({
                    value: agent.id.toString(),
                    label: agent.name
                  }))
                ]}
              />
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="dso-leads-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {leads.length === 0 ? (
            <div className="dso-leads-empty">
              <p>No leads found with the current filters.</p>
            </div>
          ) : (
            <>
              <div className="dso-leads-table-container">
                <Table responsive striped className="dso-leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th>Product</th>
                      <th>Agent</th>
                      <th>Status</th>
                      <th>Application #</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id}>
                        <td>{lead.name}</td>
                        <td>{lead.phone}</td>
                        <td>{lead.person_type || 'Individual'}</td>
                        <td>
                          <div className="dso-lead-product">
                            <span className="dso-lead-product-name">{lead.product}</span>
                            <span className="dso-lead-product-type">{caseTypeMap[lead.case_type] || lead.case_type}</span>
                          </div>
                        </td>
                        <td>{lead.agent}</td>
                        <td>
                          <Badge 
                            bg={caseStatusMap[lead.case_status]?.variant || 'secondary'}
                            className="dso-lead-status"
                          >
                            {lead.case_status_str}
                          </Badge>
                        </td>
                        <td>
                          {lead.application_no || 'N/A'}
                        </td>
                        <td>
                          <div>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleViewLead(lead.lead_id)}
                            >
                              <FaEye /> View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {renderPagination()}
            </>
          )}
        </>
      )}
    </div>

     {/* Render the AddLeadForm component */}
     <AddLeadForm
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSubmit={handleAddLeadSubmit}
        agents={agents}
        loadingAgents={loadingAgents}
        products={products}
        loadingProducts={loadingProducts}
        fetchProducts={fetchProducts}
     />

     {/* Render the new BulkUploadModal component */}
     <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUploadSubmit}
        agents={agents}
        loadingAgents={loadingAgents}
     />
    </React.Fragment>
  );
}

export default DSOLeads;
